import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import ListSubheader from '@mui/material/ListSubheader';

import { getContrastYIQ } from 'src/pages/chat/components/shared/misc/chat-label';

// ----------------------------------------------------------------------

type HeadFilterCustomFieldProps = {
  filterValue: any;
  setFilterValue: (value: any) => void;
  selectedColumn: { id?: string; label?: string } | null;
  externalData: {
    labelsList: any[];
    isLabelsFetching: boolean;
    openLabelsList: boolean;
    labelsListScrollHandler: any;
    labelsSearchValueHandler: any;
    openLabelsListHandler: any;
    closeLabelsListHandler: any;
    lifecycleList: any[];
    isLifecycleFetching: boolean;
    openLifecycleList: boolean;
    lifecycleListScrollHandler: any;
    lifecycleSearchValueHandler: any;
    openLifecycleListHandler: any;
    closeLifecycleListHandler: any;
  };
  t: (key: string) => string;
};

// ----------------------------------------------------------------------

export function HeadFilterCustomField({
  filterValue,
  setFilterValue,
  selectedColumn,
  externalData,
  t,
}: HeadFilterCustomFieldProps) {
  const isLabel = selectedColumn?.id === 'labels';

  const currentList = isLabel ? externalData.labelsList : externalData.lifecycleList;
  const isFetching = isLabel ? externalData.isLabelsFetching : externalData.isLifecycleFetching;
  const openList = isLabel ? externalData.openLabelsList : externalData.openLifecycleList;
  const listScrollHandler = isLabel
    ? externalData.labelsListScrollHandler
    : externalData.lifecycleListScrollHandler;
  const searchValueHandler = isLabel
    ? externalData.labelsSearchValueHandler
    : externalData.lifecycleSearchValueHandler;
  const openListHandler = isLabel
    ? externalData.openLabelsListHandler
    : externalData.openLifecycleListHandler;
  const closeListHandler = isLabel
    ? externalData.closeLabelsListHandler
    : externalData.closeLifecycleListHandler;

  return (
    <Autocomplete
      disableClearable
      open={openList}
      onOpen={openListHandler}
      onClose={closeListHandler}
      options={currentList}
      value={filterValue}
      loading={isFetching}
      noOptionsText={t('NO_OPTIONS')}
      loadingText={t('LOADING')}
      groupBy={isLabel ? undefined : (option: any) => option?.type || 'Other'}
      renderGroup={
        isLabel
          ? undefined
          : (params) => (
              <li key={params.key}>
                <ListSubheader
                  disableSticky
                  sx={{
                    fontWeight: 600,
                    fontSize: 13,
                    lineHeight: '32px',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {t(params.group)} {t('STAGES')}
                </ListSubheader>
                {params.children}
              </li>
            )
      }
      slotProps={{
        listbox: {
          onScroll: listScrollHandler,
          sx: {
            maxHeight: 200,
            overflowY: 'auto',
          },
        },
      }}
      onChange={(e, newValue) => {
        if (newValue) setFilterValue(newValue);
      }}
      onInputChange={(e, val, reason) => {
        if (reason === 'input') searchValueHandler(val);
      }}
      getOptionLabel={(option: any) => option?.labelName || option?.name || ''}
      isOptionEqualToValue={(option: any, val: any) => option?.id === val?.id}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={isLabel ? t('LABELS') : t('LIFECYCLE_STAGE')}
          placeholder={t('SEARCH')}
        />
      )}
      renderOption={(props, option) => (
        <MenuItem
          {...props}
          key={option?.id}
          disabled={option?.id === 'no-label'}
          sx={
            isLabel
              ? {
                  backgroundColor: option?.labelColor,
                  color: getContrastYIQ(option?.labelColor),
                  padding: '8px',
                }
              : { pl: 3 }
          }
        >
          {option?.labelName || t(option?.name)}
        </MenuItem>
      )}
      fullWidth
    />
  );
}
